"use client";

import { useState } from "react";
import BFLModal from "@repo/shared-ui/BFLModal";
import { IShareholder } from "../../interfaces/StockShareholding.interface";
import { handleShareholdingPatternButtonDL } from "../StockFinancials/BFLTabsFunction";
import { selectLoginStatus, useAppSelector } from "@repo/web-store";
import { sortObjectByKeySuffix } from "@repo/shared-utils/common-utils";

const formatNumber = (num: number) => num.toLocaleString("en-IN");
interface TableHeaderItem {
  tablecolumntitletext: string;
}

const ShareholdingSummaryTable = ({
  data = [],
  tableHeader = {},
  modalContent,
}: {
  data: IShareholder[];
  tableHeader: Record<string, { tablecolumntitletext: string }>;
  modalContent: {
    buttonText: string;
    title: string;
  };
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const headers = Object.values(tableHeader);
  const loginStatus = useAppSelector(selectLoginStatus);

  /**
   * Handle View All button click with GA tracking
   */
  const handleViewAllClick = () => {
    // Track the CTA click
    handleShareholdingPatternButtonDL(loginStatus, {
      sectionTitle: modalContent?.title || "Shareholding Pattern",
      ctaText: "View All",
    });

    // Open the modal
    setIsModalOpen(true);
  };

  /**
   * Handle modal close with GA tracking
   */
  const handleModalClose = () => {
    // Track the modal close
    handleShareholdingPatternButtonDL(loginStatus, {
      sectionTitle: modalContent?.title || "Shareholding Pattern",
      ctaText: "Close Modal",
    });

    // Close the modal
    setIsModalOpen(false);
  };
  const sortedHeaders =
    (sortObjectByKeySuffix(tableHeader || {}, "item") as TableHeaderItem[]) ||
    [];

  const getModalColumnWidth = (index: number): string => {
    if (index === 0) return "w-[163px]";
    if (index === 1) return "w-[130px]";
    return "w-auto";
  };

  const viewAllModal = () => {
    return (
      <div className="relative mr-[-8px] max-h-[455px] overflow-x-auto md:max-h-[306px] md:[-ms-overflow-style:none] md:[scrollbar-width:none] md:[&::-webkit-scrollbar]:hidden">
        <table className="w-[104%] table-fixed border-separate border-spacing-0 border border-gray-200 text-left md:w-full">
          <thead className="bg-lgray-50 sticky top-0 z-20 text-sm font-medium text-gray-700">
            <tr>
              {headers.map((item) => (
                <th
                  key={item.tablecolumntitletext}
                  className={`${getModalColumnWidth(headers.indexOf(item))} whitespace-nowrap border-r border-gray-200 px-2 py-2 text-xs font-[500] text-gray-900 ${headers.indexOf(item) === 0 ? "bg-lgray-50 sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" : "bg-lgray-50"}`}
                >
                  {item?.tablecolumntitletext}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-xs text-gray-800">
            {data?.map((item, idx) => {
              const hasZeroShares = item?.shares === 0;
              const sharesDisplay = hasZeroShares
                ? "-"
                : `${formatNumber(item.shares)} (${item.sharePer}%)`;

              return (
                <tr
                  key={`${item.name}-${idx}`}
                  className="even:bg-lgray-50 border-b border-gray-200 odd:bg-white"
                >
                  <td
                    className={`sticky left-0 z-10 w-[163px] whitespace-normal break-words border-r border-gray-200 px-2 py-3 text-sm font-[500] text-gray-900 ${idx % 2 === 0 ? "bg-white" : "bg-lgray-50"} shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]`}
                  >
                    {item.name}
                  </td>
                  <td className="w-[130px] border-r border-gray-200 px-3 py-[17.9px] text-sm">
                    {sharesDisplay}
                  </td>
                  <td className="w-auto whitespace-normal break-words px-3 py-[17.9px] text-sm leading-tight">
                    {item.category}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="mt-2 flex w-full flex-col overflow-hidden rounded md:mt-0">
      {/* Main Table */}
      <div className="w-full overflow-x-auto">
        <table className="w-full min-w-[500px] border-separate border-spacing-0 rounded-md border border-gray-200 text-left">
          <thead className="bg-lgray-50 text-sm font-medium text-gray-700">
            <tr>
              {sortedHeaders.map((item, index) => (
                <th
                  key={item?.tablecolumntitletext}
                  className={`whitespace-nowrap border-r border-gray-200 px-3 py-3 text-xs font-[500] text-gray-900 ${item?.tablecolumntitletext === "Share" || item?.tablecolumntitletext === "Category" ? "md:w-[24%]" : "md:w-auto"} ${index === 0 ? "sticky left-0 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" : ""} bg-lgray-50 w-[50px]`}
                >
                  {item?.tablecolumntitletext}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-xs text-gray-800">
            {data?.slice(0, 5)?.map((entry, idx) => {
              const sharesDisplay =
                entry?.shares === 0
                  ? "-"
                  : `${formatNumber(entry.shares)} (${entry.sharePer}%)`;

              return (
                <tr
                  key={entry.name}
                  className="even:bg-lgray-50 border-b border-gray-200 odd:bg-white"
                >
                  {/* Promoter shares */}
                  <td
                    className={`sticky left-0 break-words border-r border-gray-200 px-2 py-3 text-sm font-[500] text-gray-900 ${idx % 2 === 0 ? "bg-white" : "bg-lgray-50"} w-[50px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] md:w-[24%]`}
                    title={entry.name}
                  >
                    <h4 className="mb-2 mt-0 box-border font-medium leading-6 text-[#1a1a1a]">
                      {entry.name}
                    </h4>
                  </td>

                  {/* Share */}
                  <td className="border-r border-gray-200 px-3 py-3 md:w-[24%]">
                    <h4 className="mb-2 mt-0 box-border text-sm leading-6 text-[#1a1a1a]">
                      {sharesDisplay}
                    </h4>
                  </td>

                  {/* Category */}
                  <td
                    className="break-words px-2 py-3 leading-tight md:w-auto"
                    title={entry.category}
                  >
                    <h4 className="mb-2 mt-0 box-border text-sm leading-6 text-[#1a1a1a]">
                      {entry.category}
                    </h4>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-2">
        <button
          onClick={handleViewAllClick}
          className="cursor-pointer text-sm text-[#EF6C00]"
        >
          View All
        </button>
      </div>

      {/* Modal (UNCHANGED) */}
      <BFLModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={modalContent?.title}
        description=""
        contentClassName=""
        buttonText={modalContent?.buttonText}
      >
        {viewAllModal()}
      </BFLModal>

      <div style={{ display: isModalOpen ? "hidden" : "none" }}>
        {viewAllModal()}
      </div>
    </div>
  );
};

export default ShareholdingSummaryTable;
